import {Injectable} from '@angular/core';
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {VisitLabTestDataDgSearchResults} from "../models/datagrid-search-results/visit-lab-test-data.dg-search-results.model";
import {VisitLabTestData} from "../models/visit-lab-test-data.model";
import {LabTestDataObj} from "../models/lab-test-data-obj.model";

/**
 * Serwis warstwy danych zarządzający wynikami badań laboratoryjnych pacjentów.
 * Odpowiada za integrację surowych danych analitycznych z odpowiednimi wizytami
 * (VetReservation) oraz za zasilanie widoków tabelarycznych (DataGrid).
 */
@Injectable({
  providedIn: 'root'
})
export class VisitLabTestDataService implements DataGridServiceInterface<Observable<VisitLabTestDataDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę wyników badań laboratoryjnych dla komponentu DataGrid.
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników względem początku zestawu danych.
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Kolumna sortująca (np. data wykonania badania).
   * @param showDeletedObjects Flaga uwzględniająca usunięte/anulowane wyniki.
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla tabeli.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<VisitLabTestDataDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/lab-test-data/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new VisitLabTestDataDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VisitLabTestData(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje wyniki badań laboratoryjnych na podstawie zdefiniowanych filtrów.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca parametry wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowaną listę dla DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<VisitLabTestDataDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/lab-test-data/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new VisitLabTestDataDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VisitLabTestData(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełen, ustrukturyzowany model wyników badań laboratoryjnych
   * na podstawie ich unikalnego identyfikatora.
   *
   * @param uuid Identyfikator rekordu badania laboratoryjnego.
   * @returns Observable emitujący obiekt szczegółów badania (VisitLabTestData).
   */
  getLabTestData(uuid: string): Observable<VisitLabTestData> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/lab-test-data/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VisitLabTestData>(new VisitLabTestData(), vetineoApiResponse.response.visit);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera dynamiczny i zdenormalizowany obiekt JSON z wynikami badań dla konkretnej wizyty.
   * Metoda zoptymalizowana do renderowania niestandardowych struktur danych,
   * gdzie zestaw parametrów jest wysoce zmienny (w zależności od rodzaju badania).
   *
   * @param visitUUID Identyfikator wizyty weterynaryjnej, do której przypięto badania.
   * @returns Observable emitujący surowy obiekt danych laboratoryjnych (LabTestDataObj).
   */
  getLabTestDataJSON(visitUUID): Observable<LabTestDataObj> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/lab-test-data-json/${visitUUID}`).pipe(map(vetineoApiResponse => {
      console.log(vetineoApiResponse)
      if (vetineoApiResponse.ok && vetineoApiResponse.response.labTestData) {
        return this.commonUtils.bestTypedCopyEver<LabTestDataObj>(new LabTestDataObj(), vetineoApiResponse.response.labTestData);
      }
    }));
  }

  /**
   * Zapisuje komplet wyników badań laboratoryjnych w bazie danych.
   *
   * @param labTestData Obiekt zawierający wyniki do zapisu (powiązane z wizytą).
   * @returns Observable emitujący zaktualizowany obiekt zwrócony przez serwer.
   */
  sendLabTestData(labTestData: VisitLabTestData) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/lab-test-db-data', labTestData).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response)
        return this.commonUtils.bestTypedCopyEver<VisitLabTestData>(new VisitLabTestData(), vetineoApiResponse.response.visit);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera agregat (pełną historię) wszystkich badań laboratoryjnych
   * wykonanych u danego użytkownika/pacjenta.
   *
   * @param uuid Unikalny identyfikator właściciela/pacjenta.
   * @returns Observable emitujący tablicę wszystkich zrealizowanych badań.
   */
  getLabTestDataForUser(uuid: any) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/lab-test-data/by-user/${uuid}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VisitLabTestData(), [], vetineoApiResponse.response.labTestData);
      } else {
        return undefined;
      }
    }));
  }
}
